import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Link2, ExternalLink, Box, Save, Send, Printer, Pencil, Unlink, User, Package, XCircle } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent, Badge } from '@components/Card';
import { FreightQuotePanel, FreightQuoteItem } from '@components/FreightQuotePanel';
import { usePermissions } from '@/hooks/usePermissions';
import eventService from '@services/eventService';
import {
  DeliveryOrder,
  DeliveryOrderVolume,
  DeliveryOrderTracking,
  DELIVERY_ORDER_STATUS,
} from '@/types/event';
import { useCepSearch } from '@/hooks/useCepSearch';
import {
  statusBadgeVariant,
  canEmitInvoice,
  canDispatchCorreios,
  canPrintInvitationLabels,
  canUnlinkInvoice,
  isOwnLogistics,
  missingInvoiceFields,
  orderToRecipientForm,
  recipientFormToPatch,
  CORREIOS_PROVIDER_ID,
  InvitationRecipientForm,
} from './invitationUtils';

const formatDateTimeBR = (iso?: string) => {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : iso;
};

// Detalhe do convite: convidado, volume, cotação de transporte (mesmo painel do pedido
// de venda), NF de bonificação (só transportadora — CFOP 5.949/6.949) e rastreio.
export const InvitationDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const deliveryOrderID = Number(id);
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('events.write').hasPermission || checkPermission('*').hasPermission;

  const [order, setOrder] = useState<DeliveryOrder | null>(null);
  const [volumes, setVolumes] = useState<DeliveryOrderVolume[]>([]);
  const [tracking, setTracking] = useState<DeliveryOrderTracking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Volume (edição inline enquanto Pendente)
  const [volumeEdit, setVolumeEdit] = useState<{ height: string; width: string; length: string; weight: string } | null>(null);
  const [volumeSaving, setVolumeSaving] = useState(false);

  // Cotação de frete
  const [quotes, setQuotes] = useState<FreightQuoteItem[]>([]);
  const [freightLoading, setFreightLoading] = useState(false);
  const [freightCalculating, setFreightCalculating] = useState(false);
  const [freightSaving, setFreightSaving] = useState(false);
  const [freightError, setFreightError] = useState<string | null>(null);

  // Vincular NF existente
  const [linkInvoiceID, setLinkInvoiceID] = useState('');
  const [linking, setLinking] = useState(false);
  const [canceling, setCanceling] = useState(false);

  // Despacho Correios em 1 clique + impressão das etiquetas combinadas
  const [dispatching, setDispatching] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  // Erro do card de NF/despacho: fica JUNTO do botão. O aviso do topo da página passa
  // despercebido — a ação está no fim de uma tela longa.
  const [dispatchError, setDispatchError] = useState<string | null>(null);

  // Edição do convidado (dados + endereço de entrega). O snapshot é copiado do CRM na
  // criação do convite, mas o cadastro de origem costuma vir sem CPF/CNPJ — que a NF de
  // bonificação exige — e o endereço muda. O PATCH sempre aceitou estes campos; faltava
  // a tela: antes só dava para cancelar o convite e criar outro.
  const [recipientEdit, setRecipientEdit] = useState<InvitationRecipientForm | null>(null);
  const [recipientSaving, setRecipientSaving] = useState(false);
  const { searchCep } = useCepSearch();

  const mapQuotes = (data: { delivery_order_freight_quote_id: number; provider_id?: number; provider_service_id?: number; provider_name?: string; provider_service_name?: string; cost?: number; delivery_days?: number; is_selected: boolean; is_manual?: boolean; blocked?: boolean; blocked_reason?: string | null }[]): FreightQuoteItem[] =>
    data.map((q) => ({
      id: q.delivery_order_freight_quote_id,
      provider_id: q.provider_id,
      provider_service_id: q.provider_service_id,
      provider_name: q.provider_name,
      provider_service_name: q.provider_service_name,
      cost: q.cost,
      delivery_days: q.delivery_days,
      blocked: q.blocked,
      blocked_reason: q.blocked_reason,
      is_selected: q.is_selected,
      is_manual: q.is_manual,
    }));

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [o, vols, trk] = await Promise.all([
        eventService.getDeliveryOrder(deliveryOrderID),
        eventService.getVolumes(deliveryOrderID),
        eventService.getTracking(deliveryOrderID).catch(() => null),
      ]);
      setOrder(o);
      setVolumes(vols);
      setTracking(trk);
      setFreightLoading(true);
      eventService.getFreightQuotes(deliveryOrderID)
        .then((q) => setQuotes(mapQuotes(q)))
        .catch(() => setQuotes([]))
        .finally(() => setFreightLoading(false));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar convite');
    } finally {
      setLoading(false);
    }
  }, [deliveryOrderID]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const isPending = order?.status_id === DELIVERY_ORDER_STATUS.PENDING;

  const handleCalculateFreight = async () => {
    try {
      setFreightCalculating(true);
      setFreightError(null);
      const q = await eventService.calculateFreightQuotes(deliveryOrderID);
      setQuotes(mapQuotes(q));
    } catch (err) {
      setFreightError(err instanceof Error ? err.message : 'Falha ao calcular cotações');
    } finally {
      setFreightCalculating(false);
    }
  };

  const handleSelectQuote = async (quoteId: number) => {
    try {
      setFreightSaving(true);
      setFreightError(null);
      const q = await eventService.selectFreightQuote(deliveryOrderID, quoteId);
      setQuotes(mapQuotes(q));
      // A escolha carimba provider/serviço no convite — recarrega o cabeçalho.
      const o = await eventService.getDeliveryOrder(deliveryOrderID);
      setOrder(o);
    } catch (err) {
      setFreightError(err instanceof Error ? err.message : 'Falha ao selecionar cotação');
    } finally {
      setFreightSaving(false);
    }
  };

  const handleSaveVolume = async () => {
    if (!volumeEdit || volumes.length === 0) return;
    try {
      setVolumeSaving(true);
      setError(null);
      const updated = await eventService.updateVolume(deliveryOrderID, volumes[0].delivery_order_volume_id, {
        height: Number(volumeEdit.height) || 0,
        width: Number(volumeEdit.width) || 0,
        length: Number(volumeEdit.length) || 0,
        diameter: 0,
        weight: Number(volumeEdit.weight) || 0,
      });
      setVolumes(updated);
      setVolumeEdit(null);
      setQuotes([]); // volume mudou ⇒ cotações foram invalidadas no backend
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar volume');
    } finally {
      setVolumeSaving(false);
    }
  };

  // Despacho Correios em 1 clique: emite a NF de bonificação e cria a pré-postagem.
  // Idempotente no backend — reclicar retoma do passo que faltou. Ao concluir, recarrega
  // o convite e já baixa o PDF combinado das etiquetas (best-effort).
  const handleDispatchCorreios = async () => {
    try {
      setDispatching(true);
      setDispatchError(null);
      await eventService.dispatchCorreios(deliveryOrderID);
      await loadAll();
      await eventService.downloadInvitationLabels(deliveryOrderID).catch(() => {
        // Etiqueta é reimprimível pelo botão — não transforma o despacho OK em erro.
      });
    } catch (err) {
      setDispatchError(err instanceof Error ? err.message : 'Falha no despacho do convite pelos Correios');
    } finally {
      setDispatching(false);
    }
  };

  // Desvincula a NF cancelada, liberando o convite para uma nova emissão. O backend só
  // aceita quando a nota está mesmo cancelada.
  const handleUnlinkInvoice = async () => {
    if (!window.confirm('Desvincular a nota fiscal cancelada deste convite?')) return;
    try {
      setUnlinking(true);
      setDispatchError(null);
      const o = await eventService.updateDeliveryOrder(deliveryOrderID, { unlink_invoice: true });
      setOrder(o);
      const trk = await eventService.getTracking(deliveryOrderID).catch(() => null);
      setTracking(trk);
    } catch (err) {
      setDispatchError(err instanceof Error ? err.message : 'Erro ao desvincular a nota fiscal');
    } finally {
      setUnlinking(false);
    }
  };

  const handlePrintLabels = async () => {
    try {
      setPrinting(true);
      setDispatchError(null);
      await eventService.downloadInvitationLabels(deliveryOrderID);
    } catch (err) {
      setDispatchError(err instanceof Error ? err.message : 'Falha ao baixar as etiquetas do convite');
    } finally {
      setPrinting(false);
    }
  };

  const setRecipientField = (patch: Partial<InvitationRecipientForm>) =>
    setRecipientEdit((cur) => (cur ? { ...cur, ...patch } : cur));

  // CEP preenche cidade/UF (sempre) e logradouro/bairro só se estiverem vazios — mesma
  // regra do formulário de criação do convite.
  const handleCepBlur = async () => {
    if (!recipientEdit) return;
    const clean = recipientEdit.ship_addr_zip.replace(/\D/g, '');
    if (clean.length !== 8) return;
    const result = await searchCep(clean);
    if (!result || result.erro) return;
    setRecipientField({
      ship_addr_street: recipientEdit.ship_addr_street || result.logradouro,
      ship_addr_district: recipientEdit.ship_addr_district || result.bairro,
      ship_addr_city: result.localidade,
      ship_addr_state: result.uf,
    });
  };

  const handleSaveRecipient = async () => {
    if (!recipientEdit) return;
    if (!recipientEdit.recipient_name.trim()) {
      setError('Informe o nome do convidado.');
      return;
    }
    const doc = recipientEdit.recipient_document.replace(/\D/g, '');
    if (doc && doc.length !== 11 && doc.length !== 14) {
      setError('CPF deve ter 11 dígitos e CNPJ 14.');
      return;
    }
    try {
      setRecipientSaving(true);
      setError(null);
      const o = await eventService.updateDeliveryOrder(deliveryOrderID, recipientFormToPatch(recipientEdit));
      setOrder(o);
      setRecipientEdit(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar os dados do convidado');
    } finally {
      setRecipientSaving(false);
    }
  };

  const handleEmitInvoice = async () => {
    if (!order) return;
    // Produto do convite e empresa emissora vêm do cadastro do evento — o produto vira o
    // item único da NF e a empresa é pré-selecionada (e travada) na tela de emissão.
    let invitationProductSku: string | undefined;
    let eventCompanyId: number | undefined;
    if (order.event_id) {
      const ev = await eventService.getEvent(order.event_id).catch(() => null);
      invitationProductSku = ev?.invitation_product_sku || undefined;
      eventCompanyId = ev?.company_id || undefined;
    }
    // NF de bonificação MANUAL (sem pedido de venda), pré-preenchida com o convite —
    // natureza "Outra Saída de Mercadoria" (CFOP 5.949/6.949) pré-selecionada pela
    // CFOPOperation. O vínculo volta via delivery_order_id no payload da emissão.
    navigate('/invoices/new', {
      state: {
        deliveryOrderPrefill: {
          invitation_product_sku: invitationProductSku,
          company_id: eventCompanyId,
          provider_id: order.provider_id,
          provider_service_id: order.provider_service_id,
          delivery_order_id: order.delivery_order_id,
          event_id: order.event_id,
          recipient_name: order.recipient_name,
          recipient_document: order.recipient_document,
          recipient_phone: order.recipient_phone,
          recipient_email: order.recipient_email,
          ship_addr_street: order.ship_addr_street,
          ship_addr_num: order.ship_addr_num,
          ship_addr_compl: order.ship_addr_compl,
          ship_addr_district: order.ship_addr_district,
          ship_addr_city: order.ship_addr_city,
          ship_addr_state: order.ship_addr_state,
          ship_addr_zip: order.ship_addr_zip,
        },
      },
    });
  };

  const handleLinkInvoice = async () => {
    const invId = Number(linkInvoiceID);
    if (!invId) return;
    try {
      setLinking(true);
      setError(null);
      const o = await eventService.updateDeliveryOrder(deliveryOrderID, { invoice_id: invId });
      setOrder(o);
      setLinkInvoiceID('');
      const trk = await eventService.getTracking(deliveryOrderID).catch(() => null);
      setTracking(trk);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao vincular NF');
    } finally {
      setLinking(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancelar este convite?')) return;
    try {
      setCanceling(true);
      const o = await eventService.updateDeliveryOrder(deliveryOrderID, { cancel: true });
      setOrder(o);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao cancelar convite');
    } finally {
      setCanceling(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-slate-500">Carregando convite...</div>;
  if (!order) return <div className="py-12 text-center text-slate-500">{error || 'Convite não encontrado'}</div>;

  const ownLogistics = isOwnLogistics(order);
  // Campos que a NF de bonificação exige e ainda faltam no convite — avisa no card do
  // convidado (onde se corrige) em vez de deixar o usuário descobrir no erro da emissão.
  const pendingInvoiceFields = order.invoice_id ? [] : missingInvoiceFields(order);
  // Com NF emitida os dados do destinatário estão congelados no documento fiscal:
  // editá-los aqui deixaria convite e nota divergentes.
  const canEditRecipient = canWrite && isPending && !order.invoice_id;
  // NF cancelada: o convite fica preso a ela até ser desvinculada.
  const invoiceCanceled = canUnlinkInvoice(order);

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" onClick={() => navigate('/events/invitations')} icon={<ArrowLeft size={16} />} />
          <div>
            <h1 className="text-h1 flex items-center gap-3">
              Convite #{order.delivery_order_id}
              <Badge variant={statusBadgeVariant(order.status_id)}>{order.status_label}</Badge>
            </h1>
            <p className="text-slate-600">{order.event_name || 'Evento não informado'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canWrite && isPending && (
            <Button variant="outline" onClick={handleCancel} disabled={canceling} icon={<XCircle size={16} className="text-red-500" />}>
              Cancelar Convite
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      {/* Convidado — dados e endereço editáveis enquanto o convite está Pendente */}
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><User size={16} className="text-emerald-600" /> Convidado</span>}
          subtitle={pendingInvoiceFields.length > 0 ? `Falta para emitir a NF: ${pendingInvoiceFields.join(', ')}.` : undefined}
          action={
            canEditRecipient && !recipientEdit ? (
              <Button size="sm" variant="outline" onClick={() => setRecipientEdit(orderToRecipientForm(order))} icon={<Pencil size={14} />}>
                Editar Dados
              </Button>
            ) : undefined
          }
        />
        <CardContent>
          {recipientEdit ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nome *</label>
                  <Input value={recipientEdit.recipient_name} onChange={(e) => setRecipientField({ recipient_name: e.target.value })} maxLength={150} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">CPF/CNPJ</label>
                  <Input
                    value={recipientEdit.recipient_document}
                    onChange={(e) => setRecipientField({ recipient_document: e.target.value.replace(/\D/g, '').slice(0, 14) })}
                    placeholder="Somente números"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Celular</label>
                  <Input value={recipientEdit.recipient_phone} onChange={(e) => setRecipientField({ recipient_phone: e.target.value })} maxLength={20} />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">E-mail</label>
                  <Input type="email" value={recipientEdit.recipient_email} onChange={(e) => setRecipientField({ recipient_email: e.target.value })} maxLength={150} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">CEP</label>
                  <Input value={recipientEdit.ship_addr_zip} onChange={(e) => setRecipientField({ ship_addr_zip: e.target.value })} onBlur={handleCepBlur} maxLength={9} placeholder="00000-000" />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Logradouro</label>
                  <Input value={recipientEdit.ship_addr_street} onChange={(e) => setRecipientField({ ship_addr_street: e.target.value })} maxLength={100} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Número</label>
                  <Input value={recipientEdit.ship_addr_num} onChange={(e) => setRecipientField({ ship_addr_num: e.target.value })} maxLength={10} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Complemento</label>
                  <Input value={recipientEdit.ship_addr_compl} onChange={(e) => setRecipientField({ ship_addr_compl: e.target.value })} maxLength={100} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bairro</label>
                  <Input value={recipientEdit.ship_addr_district} onChange={(e) => setRecipientField({ ship_addr_district: e.target.value })} maxLength={100} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Cidade</label>
                  <Input value={recipientEdit.ship_addr_city} onChange={(e) => setRecipientField({ ship_addr_city: e.target.value })} maxLength={100} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">UF</label>
                  <Input value={recipientEdit.ship_addr_state} onChange={(e) => setRecipientField({ ship_addr_state: e.target.value.toUpperCase().slice(0, 2) })} maxLength={2} />
                </div>
              </div>

              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveRecipient} disabled={recipientSaving} icon={<Save size={14} />}>
                  {recipientSaving ? 'Salvando...' : 'Salvar'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRecipientEdit(null)}>Cancelar</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Nome</p>
                <p className="font-medium text-slate-900">{order.recipient_name}</p>
              </div>
              <div>
                <p className="text-slate-500">CPF/CNPJ</p>
                <p className={`font-medium ${order.recipient_document ? 'text-slate-900' : 'text-amber-600'}`}>
                  {order.recipient_document || 'não informado'}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Contato</p>
                <p className="font-medium text-slate-900">{order.recipient_phone || '—'}{order.recipient_email ? ` · ${order.recipient_email}` : ''}</p>
              </div>
              <div className="md:col-span-3">
                <p className="text-slate-500">Endereço de entrega</p>
                <p className="font-medium text-slate-900">
                  {[order.ship_addr_street, order.ship_addr_num].filter(Boolean).join(', ')}
                  {order.ship_addr_compl ? ` — ${order.ship_addr_compl}` : ''}
                  {order.ship_addr_district ? ` — ${order.ship_addr_district}` : ''}
                  {order.ship_addr_city ? ` — ${order.ship_addr_city}/${order.ship_addr_state || ''}` : ''}
                  {order.ship_addr_zip ? ` — CEP ${order.ship_addr_zip}` : ''}
                </p>
              </div>
              {order.observation && (
                <div className="md:col-span-3">
                  <p className="text-slate-500">Observação</p>
                  <p className="text-slate-700">{order.observation}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Volume */}
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><Box size={16} className="text-emerald-600" /> Volume</span>}
          action={
            canWrite && isPending && volumes.length > 0 && !volumeEdit ? (
              <Button size="sm" variant="outline" onClick={() => setVolumeEdit({
                height: String(volumes[0].height), width: String(volumes[0].width),
                length: String(volumes[0].length), weight: String(volumes[0].weight),
              })}>
                Editar Volume
              </Button>
            ) : undefined
          }
        />
        <CardContent>
          {volumes.length === 0 ? (
            <p className="text-sm text-slate-400">Sem volume cadastrado.</p>
          ) : volumeEdit ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Altura (cm)</label>
                <Input type="number" min={0} step="0.1" value={volumeEdit.height} onChange={(e) => setVolumeEdit({ ...volumeEdit, height: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Largura (cm)</label>
                <Input type="number" min={0} step="0.1" value={volumeEdit.width} onChange={(e) => setVolumeEdit({ ...volumeEdit, width: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Comprimento (cm)</label>
                <Input type="number" min={0} step="0.1" value={volumeEdit.length} onChange={(e) => setVolumeEdit({ ...volumeEdit, length: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Peso (kg)</label>
                <Input type="number" min={0} step="0.01" value={volumeEdit.weight} onChange={(e) => setVolumeEdit({ ...volumeEdit, weight: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveVolume} disabled={volumeSaving} icon={<Save size={14} />}>Salvar</Button>
                <Button size="sm" variant="outline" onClick={() => setVolumeEdit(null)}>Cancelar</Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-700">
              {volumes[0].height} × {volumes[0].width} × {volumes[0].length} cm — <strong>{volumes[0].weight} kg</strong>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Transporte — mesmo painel de cotação do pedido de venda */}
      <FreightQuotePanel
        quotes={quotes}
        loading={freightLoading}
        calculating={freightCalculating}
        saving={freightSaving}
        error={freightError}
        readonly={!canWrite || !isPending}
        onCalculate={handleCalculateFreight}
        onSelect={handleSelectQuote}
      />

      {/* NF de bonificação + despacho */}
      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><FileText size={16} className="text-emerald-600" /> NF de Bonificação e Despacho</span>}
          subtitle='Natureza "Outra Saída de Mercadoria" (CFOP 5.949 em SP / 6.949 interestadual). Correios: NF + pré-postagem + etiquetas em 1 clique (empresa emissora vem do evento). Viver Organizer: emissão pela tela, com escolha do veículo da frota.'
        />
        <CardContent>
          {dispatchError && (
            <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{dispatchError}</div>
          )}
          {order.invoice_id ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Badge variant={invoiceCanceled ? 'danger' : 'success'}>
                  {invoiceCanceled ? 'NF cancelada' : 'NF vinculada'}
                </Badge>
                <span className="text-slate-700">InvoiceId <span className="font-mono font-medium">{order.invoice_id}</span></span>
                {order.invoice_status_label && !invoiceCanceled && (
                  <span className="text-slate-400">{order.invoice_status_label}</span>
                )}
                {canPrintInvitationLabels(order, tracking?.correios_objects?.length ?? 0) && (
                  <Button size="sm" onClick={handlePrintLabels} disabled={printing} icon={<Printer size={14} />}>
                    {printing ? 'Gerando PDF...' : 'Imprimir Etiquetas'}
                  </Button>
                )}
              </div>

              {/* NF cancelada: desvincular libera o convite para uma nova emissão. */}
              {invoiceCanceled ? (
                <div className="flex flex-wrap items-center gap-3">
                  {canWrite && (
                    <Button variant="outline" onClick={handleUnlinkInvoice} disabled={unlinking} icon={<Unlink size={16} />}>
                      {unlinking ? 'Desvinculando...' : 'Desvincular NF'}
                    </Button>
                  )}
                  <span className="text-slate-500 text-sm">
                    A nota vinculada foi cancelada — desvincule para emitir uma nova para este convite.
                  </span>
                </div>
              ) : order.provider_id === CORREIOS_PROVIDER_ID && !tracking?.correios_objects?.length && (
                canWrite && canDispatchCorreios(order) ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <Button onClick={handleDispatchCorreios} disabled={dispatching} icon={<Send size={16} />}>
                      {dispatching ? 'Despachando...' : 'Concluir despacho Correios'}
                    </Button>
                    <span className="text-slate-400 text-sm">a NF já existe — falta criar a pré-postagem nos Correios.</span>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Aguardando a pré-postagem nos Correios para liberar a impressão das etiquetas.</p>
                )
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {canWrite && canDispatchCorreios(order) && (
                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={handleDispatchCorreios} disabled={dispatching || !order.recipient_document} icon={<Send size={16} />}>
                    {dispatching ? 'Despachando...' : 'Emitir NF + Correios'}
                  </Button>
                  <span className="text-slate-500 text-sm">
                    {order.recipient_document
                      ? `Emite a NF de bonificação, cria a pré-postagem (${order.provider_name || 'Correios'}) e baixa as duas etiquetas em um único PDF.`
                      : 'Informe o CPF/CNPJ do convidado (card Convidado) para liberar o despacho.'}
                  </span>
                </div>
              )}
              {canWrite && !canDispatchCorreios(order) && (
                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={handleEmitInvoice} disabled={!canEmitInvoice(order)} icon={<FileText size={16} />}>
                    Emitir NF de Bonificação
                  </Button>
                  {ownLogistics && order.provider_id && (
                    <span className="text-slate-500 text-sm">
                      Logística própria: a tela de emissão pede o veículo (placa) que fará a entrega.
                    </span>
                  )}
                  {!order.provider_id && (
                    <span className="text-slate-400 text-sm">Selecione uma cotação de frete para liberar a emissão.</span>
                  )}
                </div>
              )}
              {canWrite && (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-slate-400 text-sm">ou vincular uma NF já emitida:</span>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="InvoiceId"
                      value={linkInvoiceID}
                      onChange={(e) => setLinkInvoiceID(e.target.value.replace(/\D/g, ''))}
                      className="w-32"
                    />
                    <Button variant="outline" size="sm" onClick={handleLinkInvoice} disabled={linking || !linkInvoiceID} icon={<Link2 size={14} />}>
                      Vincular
                    </Button>
                  </div>
                </div>
              )}
              {!order.recipient_document && (
                <p className="text-xs text-amber-600">
                  O convidado não tem CPF/CNPJ no convite — a emissão da NF vai exigir o documento do destinatário.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rastreio */}
      <Card>
        <CardHeader title={<span className="flex items-center gap-2"><Package size={16} className="text-emerald-600" /> Envio e Entrega</span>} />
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <span className="text-slate-500">Transportadora:</span>
              <span className="font-medium text-slate-900">
                {order.provider_name || (ownLogistics ? 'Viver Organizer (logística própria)' : 'A definir')}
              </span>
            </div>

            {tracking?.correios_objects && tracking.correios_objects.length > 0 && (
              <div>
                <p className="text-slate-500 mb-1">Objetos nos Correios (um por volume):</p>
                <div className="space-y-1">
                  {tracking.correios_objects.map((obj) => (
                    <div key={obj.codigo_objeto} className="flex items-center gap-3">
                      <span className="font-mono text-slate-900">{obj.codigo_objeto}</span>
                      <span className="text-xs text-slate-500">Volume {obj.volume_number}/{obj.total_volumes}</span>
                      {obj.tracking_status && <Badge variant="info">{obj.tracking_status}</Badge>}
                      <a href={obj.tracking_url} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline inline-flex items-center gap-1 text-xs">
                        Rastrear <ExternalLink size={12} />
                      </a>
                    </div>
                  ))}
                </div>
                {tracking.correios_tracking_url && (
                  <a href={tracking.correios_tracking_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-emerald-700 font-medium hover:underline">
                    Rastrear todos os objetos nos Correios <ExternalLink size={14} />
                  </a>
                )}
              </div>
            )}

            {order.status_id === DELIVERY_ORDER_STATUS.DELIVERED && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="font-medium text-emerald-800">Entregue em {formatDateTimeBR(tracking?.delivered_at || order.delivered_at)}</p>
                {tracking?.delivery_contact && (
                  <p className="text-emerald-700 mt-1">
                    Recebido por: <strong>{tracking.delivery_contact}</strong>
                    {tracking.delivery_contact_document ? ` (${tracking.delivery_contact_document})` : ''}
                  </p>
                )}
                {tracking?.delivery_photo_urls && tracking.delivery_photo_urls.length > 0 && (
                  <div className="flex gap-3 mt-2">
                    {tracking.delivery_photo_urls.map((url, i) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer" className="text-emerald-700 text-xs hover:underline inline-flex items-center gap-1">
                        Foto da entrega {i + 1} <ExternalLink size={12} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            {order.status_id === DELIVERY_ORDER_STATUS.SHIPPED && !tracking?.correios_objects?.length && (
              <p className="text-slate-600">Convite em rota de entrega (logística própria).</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
